import { ColDef, ColGroupDef } from 'ag-grid-community'
import { Negotiation, NegotiationPackageDetail, NegotiationSide, NegotiationUpdate } from 'api'
import { isEqual, isNil, isObject } from 'lodash'
import { getMetaValueForWindow } from 'model'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { createSelectedGridSettingsSelector } from 'selectors/gridSelectors'
import {
  createGetValueGetters,
  getCellEditorParams,
  getCellRendererParams,
  getEditable,
  getFrameworkRenderers,
  getHeaderComponentParams,
  negotiationRepositoryEntryGetterSelector,
  ValueGetterMapper
} from 'selectors/negotiations'
import { GlobalState } from 'store'
import { RepositoryEntry } from 'store/repository'
import { getCurrentWindowId } from 'utils'
import { isNotNil } from 'utils/predicate'
import { GridId, isCellRendererClass, isColGroupDef, NegotiationPivotedRowCellRenderer } from 'view/components/datagrid'
import { hasNegotiations, NEGOTIATIONS_PATH } from 'view/components/datagrid/renderers/negotiationDataUtil'
import { getNegotiatedValue } from '../../shared/utils'
import { curveItemNegotiationsSelector, getAllTermsForCurveNegotiationsSelector } from './CurveNegotiationSelectors'
import {
  CURVE_COLUMNS_COMMON_POSTFIX,
  CURVE_COLUMNS_COMMON_PREFIX,
  CURVE_COLUMNS_TERM_GROUP,
  CURVE_POSTFIX_GROUP_COLUMN_HEADER
} from './CurveNegotiationsGridModel'
import { GridTermColumnGroupHeader } from './TermColumnsGroupHeader'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { LiveGridColumnId } from '../../LiveNegotiationsGrid/LiveGridColumnNames'

export const createValueGetterTermDataMapper: (termId: string) => ValueGetterMapper = termId => params => {
  const { data, node, colDef } = params
  if (isNil(colDef) || isNil(data)) {
    return params
  }

  const mappedData = hasNegotiations(data) ? data[NEGOTIATIONS_PATH][termId] : undefined
  const mappedNodeData = hasNegotiations(node.data) ? node.data[NEGOTIATIONS_PATH][termId] : undefined

  return {
    ...params,
    node: {
      ...params.node,
      data: mappedNodeData
    },
    data: mappedData
  }
}

const createGetTermColId = (termId: string) => (colId: string) => `${termId}_${colId}`
const getCellRenderer = ({ cellRenderer }: ColDef) => cellRenderer && isCellRendererClass(cellRenderer) && NegotiationPivotedRowCellRenderer

export const createTermColumnMapper = (termId: string) => {
  const defaultBaseColDef: ColDef = {
    enableRowGroup: false,
    rowGroup: false
  }

  return (termColDef: ColGroupDef | ColDef, index: number): ColGroupDef | ColDef => {
    const columnGroupShow = index >= 4 ? 'open' : undefined

    if (isColGroupDef(termColDef)) {
      const mapTermColumns = createTermColumnMapper(termId)
      const children = termColDef.children.map(mapTermColumns)

      return {
        ...termColDef,
        columnGroupShow,
        children
      }
    }

    const valueGetterTermDataMapper = createValueGetterTermDataMapper(termId)
    const getTermColId = createGetTermColId(termId)

    const colId = termColDef.colId && getTermColId(termColDef.colId)
    const headerComponentParams = getHeaderComponentParams(termColDef, getTermColId)
    const editable = getEditable(termColDef, valueGetterTermDataMapper)
    const cellEditorParams = getCellEditorParams(termColDef, { termId })
    const frameworkRenderers = getFrameworkRenderers(termColDef, valueGetterTermDataMapper)
    const headerClass = `${termColDef.headerClass || ''} header-cell--grouped-col`
    const valueGetters = createGetValueGetters(valueGetterTermDataMapper)(termColDef)

    const cellRendererParams = getCellRendererParams(termColDef, valueGetterTermDataMapper, { termId })
    const cellRenderer = getCellRenderer(termColDef)

    return {
      ...defaultBaseColDef,
      ...termColDef,
      ...valueGetters,
      colId,
      editable,
      headerClass,
      headerComponentParams,
      columnGroupShow,
      cellEditorParams,
      cellRenderer: frameworkRenderers.cellRenderer || cellRenderer,
      headerComponent: frameworkRenderers.headerComponent,
      cellEditor: frameworkRenderers.cellEditor,
      cellRendererParams
    }
  }
}

const curveGridTermGroupColumnDefsSelector: Selector<GlobalState, ColGroupDef[]> = createSelector(
  getAllTermsForCurveNegotiationsSelector,
  terms => {
    //@ts-ignore
    return terms.map(term => ({
      headerName: term,
      headerGroupComponent: GridTermColumnGroupHeader,
      headerGroupComponentParams: {
        term
      },
      marryChildren: true,
      children: CURVE_COLUMNS_TERM_GROUP.map(createTermColumnMapper(term)),
      headerClass: 'header-cell--grouped'
    }))
  }
)

const curveGridColDefWithMappedHideStateSelector = createSelector(
  [
    createSelectedGridSettingsSelector(
      () => GridId.CurvesGrid,
      () => 'default'
    )
  ],
  gridSettings => (col: ColDef) => {
    if (gridSettings.columnSettings.columnState?.length) {
      const hide = gridSettings.columnSettings.columnState?.find(colState => colState.colId === col.colId)?.hide
      return { ...col, hide: hide ?? col.hide }
    } else {
      return { ...col }
    }
  }
)

export const pristineColDefSelector: Selector<GlobalState, ColDef[]> = createSelector(
  curveGridTermGroupColumnDefsSelector,
  termGroupColumns => [
    ...CURVE_COLUMNS_COMMON_PREFIX,
    ...termGroupColumns.flatMap<ColDef>(t => t.children),
    ...CURVE_COLUMNS_COMMON_POSTFIX
  ]
)

export const incomingCurveNegotiationsColumnDefWithMappedHideStateSelector = createSelector(
  [curveGridTermGroupColumnDefsSelector, curveGridColDefWithMappedHideStateSelector, isAdminGatewayUserSelector],
  (termGroupColumns, curveGridColDefWithMappedHideState, isAdmin) => {
    return [
      ...CURVE_COLUMNS_COMMON_PREFIX.map(curveGridColDefWithMappedHideState),
      ...termGroupColumns.map(t => ({
        ...t,
        children: t.children.map(curveGridColDefWithMappedHideState)
      })),
      {
        headerName: CURVE_POSTFIX_GROUP_COLUMN_HEADER,
        marryChildren: true,
        headerClass: 'header-cell--hidden',
        children: CURVE_COLUMNS_COMMON_POSTFIX.map(curveGridColDefWithMappedHideState).filter(
          col => isAdmin || col.colId !== LiveGridColumnId.MY_FICC_ENTITY_SHORT_NAME
        )
      }
    ]
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual
    }
  }
)

const hiddenNegotiableFields: (keyof Negotiation)[] = ['haircutType', 'notional', 'startDate', 'endDate', 'bondPrice']

export const createModifiedNegotiaitonsByTermSelector = (term: string): Selector<GlobalState, boolean> =>
  createSelector(curveItemNegotiationsSelector, negotiations => {
    const filteredNegotiations = negotiations.filter(n => getNegotiatedValue(n.negotiation.term) === term)

    const isModified = filteredNegotiations.some(({ negotiation }) => {
      return hiddenNegotiableFields.some(fieldKey => {
        const field = negotiation[fieldKey]
        if (isObject(field) && 'responderLastValue' in field) {
          const hasResponsderModified =
            field.original !== field.negotiated &&
            field.responderLastValue !== field.negotiated &&
            field.initiatorLastValue !== field.responderLastValue

          return hasResponsderModified
        }

        return false
      })
    })

    return isModified
  })

export const CURVE_NEGOTIATION_SELECTED_META_NAME = 'isSelectedInCurveNegotationPackage'

const hasSelectedMeta = (entry: RepositoryEntry<NegotiationUpdate> | undefined) =>
  isNotNil(entry) ? getMetaValueForWindow<boolean>(entry, CURVE_NEGOTIATION_SELECTED_META_NAME, getCurrentWindowId()) ?? false : false

export const curveNegotationPackageItemSelectedSelector = createSelector(
  negotiationRepositoryEntryGetterSelector,
  getNegotiationRepositoryEntries => {
    const responderEntries = getNegotiationRepositoryEntries(NegotiationSide.RESPONDER)
    return ({ negotiationId }: Negotiation) => hasSelectedMeta(responderEntries[negotiationId])
  }
)

export const getNegotiationPackagesSelectedSelector = (negotiationPackage: NegotiationPackageDetail) =>
  createSelector(negotiationRepositoryEntryGetterSelector, getNegotiationRepositoryEntries => {
    const negotiations = Object.values(negotiationPackage[NEGOTIATIONS_PATH]).map(n => n.negotiation)
    const responderEntries = getNegotiationRepositoryEntries(NegotiationSide.RESPONDER)
    return negotiations
      .map(n => responderEntries[n.negotiationId])
      .filter(isNotNil)
      .map(hasSelectedMeta)
      .every(selected => selected === true)
  })

export const getSelectedNegotiationsForNegotiationPackageSelector = createSelector(
  negotiationRepositoryEntryGetterSelector,
  getNegotiationRepositoryEntries => {
    const responderEntries = getNegotiationRepositoryEntries(NegotiationSide.RESPONDER)
    const selectedEntityIds = Object.values(responderEntries)
      .filter(entry => getMetaValueForWindow<boolean>(entry, CURVE_NEGOTIATION_SELECTED_META_NAME, getCurrentWindowId()) === true)
      .map(e => e.entityId)
    return (negotiationPackage: NegotiationPackageDetail) =>
      (negotiationPackage &&
        negotiationPackage.negotiations &&
        Object.values(negotiationPackage.negotiations).filter(n => selectedEntityIds.includes(n.negotiation.negotiationId))) ||
      []
  }
)
